/*
 * Copyright 2026 Jason Figge
 *
 * This file is part of Chip Hippo.
 *
 * Chip Hippo is free software: you can redistribute it and/or modify it under
 * the terms of the GNU General Public License as published by the Free
 * Software Foundation, either version 3 of the License, or (at your option)
 * any later version.
 *
 * Chip Hippo is distributed in the hope that it will be useful, but WITHOUT
 * ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or
 * FITNESS FOR A PARTICULAR PURPOSE. See the GNU General Public License for
 * more details.
 *
 * You should have received a copy of the GNU General Public License along
 * with Chip Hippo. If not, see <https://www.gnu.org/licenses/>.
 */

// w65c02.js — the WDC W65C02S 8-bit microprocessor as a PURE, DOM-free state
// machine behind the STANDARD sequential contract ({ state0, step, outputs }).
// This is the CPU that makes the 65xx breadboard computer actually RUN: place it
// with ROM/RAM (and a W65C22 VIA for I/O), wire the address + data buses, and
// clock PHI2 — one bus access per clock cycle, exactly like the real part, so the
// address bus advances visibly and you can single-step.
//
// ── How it stays pure PLAIN DATA (no generators) ─────────────────────────────
// The engine threads sequential state as an opaque value and calls `outputs`
// many times per settle (must be pure) and `step` once per tick. A cycle-stepped
// 6502 needs to SUSPEND mid-instruction on every memory access — so instead of a
// generator (unserialisable, mutation-spooky) the state is just registers plus a
// small `log` of the bytes already returned for THIS instruction's accesses. Each
// cycle a clean SYNCHRONOUS 6502 interpreter re-runs from the committed
// registers, replaying `log` for accesses already done and THROWING at the first
// new access — that throw's address/data is what the CPU drives next cycle. When
// the interpreter runs to completion the instruction is done: commit the new
// registers, clear the log, and begin the next op. Re-execution is fully
// deterministic and cheap (≤7 accesses per instruction).
//
// ── Bus protocol ─────────────────────────────────────────────────────────────
// The CPU drives A0–A15 + RWB from its pending access every cycle. On a READ it
// floats D0–D7 (memory drives them) and latches, at PHI2's FALLING edge, the
// byte that was on the bus during the HIGH phase (see `step` — a PHI2-gated
// peripheral has already let go by the time the falling edge settles);
// on a WRITE it drives D0–D7 with RWB low. SYNC is high during an opcode fetch.
// Reset boots from the $FFFC/$FFFD vector; IRQ ($FFFE), NMI ($FFFA), and BRK are
// serviced at instruction boundaries. The chip powers up already IN reset, so it
// boots on Run without an explicit RESB pulse (wire RESB to hold it if you want).
//
// ── Scope (all faithful to the rest of this zero-delay sim) ───────────────────
//   • CYCLE-exact to the W65C02S datasheet: every access (address, R/W, data)
//     in order, the dummy cycles included — implied ops' read of the next
//     byte, an index's extra cycle (+1 across a page on reads, always on
//     stores), RMW's second read, taken branches, the stack ops' internal
//     cycles, decimal ADC/SBC's extra cycle, the 7-cycle reset and interrupt
//     sequences — so instruction lengths are the datasheet's. Where a dummy
//     cycle's ADDRESS is the 65C02's documented one (the last instruction
//     byte instead of the NMOS part's half-formed address, a re-read instead
//     of RMW's double write) it is used; elsewhere it is the next
//     instruction's address, which nothing decodes. RDY stretches cycles.
//   • Full documented WDC 65C02 instruction set (base 6502 + BRA, STZ, PHX/PHY/
//     PLX/PLY, INC/DEC A, (zp), JMP (abs,x), TRB/TSB, BBRx/BBSx, RMBx/SMBx, WAI,
//     STP). Undefined opcodes are NOPs of the W65C02S's own lengths (1–3
//     bytes). An IRQ is polled one instruction late across CLI/SEI/PLP, as
//     the silicon does. Decimal-mode ADC/SBC is
//     implemented (binary flags exact; BCD flag corners approximate). SOB is not
//     modelled.

import { H, L, Z } from "./levels.js";

// ── Status flags ─────────────────────────────────────────────────────────────
const C = 0x01;
const ZF = 0x02;
const I = 0x04;
const D = 0x08;
const B = 0x10;
const U = 0x20;
const V = 0x40;
const N = 0x80;

const setF = (cpu, mask, on) =>
  (cpu.p = on ? cpu.p | mask : cpu.p & ~mask & 0xff);
const getF = (cpu, mask) => (cpu.p & mask) !== 0;
const setNZ = (cpu, v) => {
  v &= 0xff;
  setF(cpu, ZF, v === 0);
  setF(cpu, N, (v & 0x80) !== 0);
};
const signed = (b) => (b < 0x80 ? b : b - 256);

// ── The suspend sentinel: thrown by the bus at the first un-replayed access ───
const SUSPEND = Symbol("cpu-suspend");

/** A replay bus over `log`: served entries return; the first new access throws. */
function makeBus(log) {
  let i = 0;
  const bus = {
    pending: null,
    read(addr) {
      addr &= 0xffff;
      if (i < log.length) return log[i++];
      bus.pending = { addr, rw: "r", dout: 0 };
      throw SUSPEND;
    },
    write(addr, val) {
      addr &= 0xffff;
      if (i < log.length) {
        i++;
        return;
      }
      bus.pending = { addr, rw: "w", dout: val & 0xff };
      throw SUSPEND;
    },
  };
  return bus;
}

// ── ALU helpers ──────────────────────────────────────────────────────────────
function adc(cpu, v) {
  const carry = getF(cpu, C) ? 1 : 0;
  if (getF(cpu, D)) {
    let lo = (cpu.a & 0x0f) + (v & 0x0f) + carry;
    let hi = (cpu.a >> 4) + (v >> 4);
    if (lo > 9) {
      lo += 6;
      hi += 1;
    }
    setF(cpu, V, (~(cpu.a ^ v) & (cpu.a ^ (hi << 4)) & 0x80) !== 0);
    if (hi > 9) hi += 6;
    setF(cpu, C, hi > 15);
    // N/Z read the final BCD-corrected byte, not the pre-correction digits —
    // e.g. $99 + $01 with carry must show Z set, N clear (rolls over to $00).
    cpu.a = ((hi << 4) | (lo & 0x0f)) & 0xff;
    setNZ(cpu, cpu.a);
  } else {
    const sum = cpu.a + v + carry;
    setF(cpu, C, sum > 0xff);
    setF(cpu, V, (~(cpu.a ^ v) & (cpu.a ^ sum) & 0x80) !== 0);
    cpu.a = sum & 0xff;
    setNZ(cpu, cpu.a);
  }
}

function sbc(cpu, v) {
  const borrow = getF(cpu, C) ? 0 : 1;
  const bin = cpu.a - v - borrow;
  if (getF(cpu, D)) {
    // The 65C02's own correction (Bruce Clark, "Decimal Mode", appendix A):
    // the binary difference, less $60 where it went below zero and $06
    // where its low digit did — the same byte as digit by digit on valid
    // BCD, and the silicon's on any other ($00 − $0A read $9F, not $8F).
    const lo = (cpu.a & 0x0f) - (v & 0x0f) - borrow;
    let r = bin;
    if (r < 0) r -= 0x60;
    if (lo < 0) r -= 0x06;
    setF(cpu, C, bin >= 0);
    setF(cpu, V, ((cpu.a ^ v) & (cpu.a ^ (bin & 0xff)) & 0x80) !== 0);
    // N/Z read the final BCD-corrected byte, not the pre-correction binary
    // difference — e.g. $00 - $01 with borrow must show N set (result $99).
    cpu.a = r & 0xff;
    setNZ(cpu, cpu.a);
  } else {
    setF(cpu, C, bin >= 0);
    setF(cpu, V, ((cpu.a ^ v) & (cpu.a ^ bin) & 0x80) !== 0);
    cpu.a = bin & 0xff;
    setNZ(cpu, cpu.a);
  }
}

function cmp(cpu, reg, v) {
  const t = reg - v;
  setF(cpu, C, reg >= v);
  setNZ(cpu, t & 0xff);
}

function bitTest(cpu, v, mode) {
  setF(cpu, ZF, (cpu.a & v) === 0);
  if (mode !== "imm") {
    setF(cpu, N, (v & 0x80) !== 0);
    setF(cpu, V, (v & 0x40) !== 0);
  }
}

/** Shift/rotate/inc/dec on a value; sets flags; returns the 8-bit result. */
function rmwValue(cpu, m, v) {
  v &= 0xff;
  let r;
  switch (m) {
    case "ASL":
      setF(cpu, C, (v & 0x80) !== 0);
      r = (v << 1) & 0xff;
      break;
    case "LSR":
      setF(cpu, C, (v & 0x01) !== 0);
      r = v >> 1;
      break;
    case "ROL":
      r = ((v << 1) | (getF(cpu, C) ? 1 : 0)) & 0xff;
      setF(cpu, C, (v & 0x80) !== 0);
      break;
    case "ROR":
      r = ((v >> 1) | (getF(cpu, C) ? 0x80 : 0)) & 0xff;
      setF(cpu, C, (v & 0x01) !== 0);
      break;
    case "INC":
      r = (v + 1) & 0xff;
      break;
    default: // DEC
      r = (v - 1) & 0xff;
      break;
  }
  setNZ(cpu, r);
  return r;
}

// ── Addressing: each returns the effective address, consuming operand bytes ───
//
// …and running the mode's DUMMY cycles (WDC W65C02S datasheet, table 5-7 and
// the opcode matrix's cycle counts). An index added to a zero-page address,
// or carried into an absolute address's high byte, costs a cycle; on the
// 65C02 that cycle READS THE LAST INSTRUCTION BYTE again (the operand byte
// just fetched, `pc - 1`) where the NMOS part read a half-formed address — so
// no stray read lands on an I/O register. `kind` says when an absolute,X/Y or
// (zp),Y address pays it:
//   "read"  — only when the index carries into the high byte;
//   "write" — always (a store cannot write until the address is whole);
//   "inc"   — always: INC/DEC abs,X are 7 cycles even within a page, where
//             the 65C02's shifts and rotates (`"read"` here) take 6 + 1.
function effectiveAddr(cpu, bus, mode, kind = "read") {
  const fetch = () => {
    const v = bus.read(cpu.pc);
    cpu.pc = (cpu.pc + 1) & 0xffff;
    return v;
  };
  const idle = () => bus.read((cpu.pc - 1) & 0xffff);
  const indexed = (base, index) => {
    const ad = (base + index) & 0xffff;
    if (kind !== "read" || (ad ^ base) & 0xff00) idle();
    return ad;
  };
  switch (mode) {
    case "imm": {
      const a = cpu.pc;
      cpu.pc = (cpu.pc + 1) & 0xffff;
      return a;
    }
    case "zp":
      return fetch();
    case "zpx": {
      const z = fetch();
      idle();
      return (z + cpu.x) & 0xff;
    }
    case "zpy": {
      const z = fetch();
      idle();
      return (z + cpu.y) & 0xff;
    }
    case "abs": {
      const lo = fetch();
      return lo | (fetch() << 8);
    }
    case "abx": {
      const lo = fetch();
      return indexed(lo | (fetch() << 8), cpu.x);
    }
    case "aby": {
      const lo = fetch();
      return indexed(lo | (fetch() << 8), cpu.y);
    }
    case "izx": {
      const z0 = fetch();
      idle();
      const z = (z0 + cpu.x) & 0xff;
      return bus.read(z) | (bus.read((z + 1) & 0xff) << 8);
    }
    case "izy": {
      const z = fetch();
      const base = bus.read(z) | (bus.read((z + 1) & 0xff) << 8);
      // The index's dummy cycle comes after the pointer is read, as on
      // abs,Y — the last instruction byte (the zero-page operand) again.
      const ad = (base + cpu.y) & 0xffff;
      if (kind !== "read" || (ad ^ base) & 0xff00) bus.read((cpu.pc - 1) & 0xffff); // prettier-ignore
      return ad;
    }
    default: {
      // "izp" — 65C02 (zp) indirect
      const z = fetch();
      return bus.read(z) | (bus.read((z + 1) & 0xff) << 8);
    }
  }
}

function push(cpu, bus, v) {
  bus.write(0x100 + (cpu.s & 0xff), v);
  cpu.s = (cpu.s - 1) & 0xff;
}
function pull(cpu, bus) {
  cpu.s = (cpu.s + 1) & 0xff;
  return bus.read(0x100 + cpu.s);
}

/**
 * Push PC + status and vector off — hardware IRQ/NMI or software BRK. Seven
 * cycles either way: a hardware interrupt opens with two reads of PC (the
 * opcode fetch it abandons, and PC again), BRK with its opcode and the read
 * of its signature byte (execInstruction's implied cycle), which it skips.
 */
function doInterrupt(cpu, bus, kind) {
  if (kind === "brk") {
    cpu.pc = (cpu.pc + 1) & 0xffff; // BRK skips a padding byte
  } else {
    bus.read(cpu.pc);
    bus.read(cpu.pc);
  }
  push(cpu, bus, (cpu.pc >> 8) & 0xff);
  push(cpu, bus, cpu.pc & 0xff);
  push(cpu, bus, kind === "brk" ? cpu.p | B | U : (cpu.p | U) & ~B);
  setF(cpu, I, true);
  setF(cpu, D, false); // the 65C02 clears decimal on an interrupt
  const vec = kind === "nmi" ? 0xfffa : 0xfffe;
  cpu.pc = bus.read(vec) | (bus.read(vec + 1) << 8);
}

// ── Opcode table: code → { m: mnemonic, a: addressing mode, bit? } ────────────
const TABLE = new Array(256).fill(null);
const set = (code, m, a, bit) => (TABLE[code] = { m, a, bit });

// Loads / stores.
for (const [op, mode] of [
  [0xa9, "imm"],
  [0xa5, "zp"],
  [0xb5, "zpx"],
  [0xad, "abs"],
  [0xbd, "abx"],
  [0xb9, "aby"],
  [0xa1, "izx"],
  [0xb1, "izy"],
  [0xb2, "izp"],
])
  set(op, "LDA", mode);
for (const [op, mode] of [
  [0xa2, "imm"],
  [0xa6, "zp"],
  [0xb6, "zpy"],
  [0xae, "abs"],
  [0xbe, "aby"],
])
  set(op, "LDX", mode);
for (const [op, mode] of [
  [0xa0, "imm"],
  [0xa4, "zp"],
  [0xb4, "zpx"],
  [0xac, "abs"],
  [0xbc, "abx"],
])
  set(op, "LDY", mode);
for (const [op, mode] of [
  [0x85, "zp"],
  [0x95, "zpx"],
  [0x8d, "abs"],
  [0x9d, "abx"],
  [0x99, "aby"],
  [0x81, "izx"],
  [0x91, "izy"],
  [0x92, "izp"],
])
  set(op, "STA", mode);
for (const [op, mode] of [
  [0x86, "zp"],
  [0x96, "zpy"],
  [0x8e, "abs"],
])
  set(op, "STX", mode);
for (const [op, mode] of [
  [0x84, "zp"],
  [0x94, "zpx"],
  [0x8c, "abs"],
])
  set(op, "STY", mode);
for (const [op, mode] of [
  [0x64, "zp"],
  [0x74, "zpx"],
  [0x9c, "abs"],
  [0x9e, "abx"],
])
  set(op, "STZ", mode);

// ALU.
for (const [mn, list] of [
  [
    "ADC",
    [
      [0x69, "imm"],
      [0x65, "zp"],
      [0x75, "zpx"],
      [0x6d, "abs"],
      [0x7d, "abx"],
      [0x79, "aby"],
      [0x61, "izx"],
      [0x71, "izy"],
      [0x72, "izp"],
    ],
  ],
  [
    "SBC",
    [
      [0xe9, "imm"],
      [0xe5, "zp"],
      [0xf5, "zpx"],
      [0xed, "abs"],
      [0xfd, "abx"],
      [0xf9, "aby"],
      [0xe1, "izx"],
      [0xf1, "izy"],
      [0xf2, "izp"],
    ],
  ],
  [
    "AND",
    [
      [0x29, "imm"],
      [0x25, "zp"],
      [0x35, "zpx"],
      [0x2d, "abs"],
      [0x3d, "abx"],
      [0x39, "aby"],
      [0x21, "izx"],
      [0x31, "izy"],
      [0x32, "izp"],
    ],
  ],
  [
    "ORA",
    [
      [0x09, "imm"],
      [0x05, "zp"],
      [0x15, "zpx"],
      [0x0d, "abs"],
      [0x1d, "abx"],
      [0x19, "aby"],
      [0x01, "izx"],
      [0x11, "izy"],
      [0x12, "izp"],
    ],
  ],
  [
    "EOR",
    [
      [0x49, "imm"],
      [0x45, "zp"],
      [0x55, "zpx"],
      [0x4d, "abs"],
      [0x5d, "abx"],
      [0x59, "aby"],
      [0x41, "izx"],
      [0x51, "izy"],
      [0x52, "izp"],
    ],
  ],
  [
    "CMP",
    [
      [0xc9, "imm"],
      [0xc5, "zp"],
      [0xd5, "zpx"],
      [0xcd, "abs"],
      [0xdd, "abx"],
      [0xd9, "aby"],
      [0xc1, "izx"],
      [0xd1, "izy"],
      [0xd2, "izp"],
    ],
  ],
  [
    "CPX",
    [
      [0xe0, "imm"],
      [0xe4, "zp"],
      [0xec, "abs"],
    ],
  ],
  [
    "CPY",
    [
      [0xc0, "imm"],
      [0xc4, "zp"],
      [0xcc, "abs"],
    ],
  ],
  [
    "BIT",
    [
      [0x89, "imm"],
      [0x24, "zp"],
      [0x34, "zpx"],
      [0x2c, "abs"],
      [0x3c, "abx"],
    ],
  ],
]) {
  for (const [op, mode] of list) set(op, mn, mode);
}

// Read-modify-write.
for (const [mn, list] of [
  [
    "ASL",
    [
      [0x0a, "acc"],
      [0x06, "zp"],
      [0x16, "zpx"],
      [0x0e, "abs"],
      [0x1e, "abx"],
    ],
  ],
  [
    "LSR",
    [
      [0x4a, "acc"],
      [0x46, "zp"],
      [0x56, "zpx"],
      [0x4e, "abs"],
      [0x5e, "abx"],
    ],
  ],
  [
    "ROL",
    [
      [0x2a, "acc"],
      [0x26, "zp"],
      [0x36, "zpx"],
      [0x2e, "abs"],
      [0x3e, "abx"],
    ],
  ],
  [
    "ROR",
    [
      [0x6a, "acc"],
      [0x66, "zp"],
      [0x76, "zpx"],
      [0x6e, "abs"],
      [0x7e, "abx"],
    ],
  ],
  [
    "INC",
    [
      [0x1a, "acc"],
      [0xe6, "zp"],
      [0xf6, "zpx"],
      [0xee, "abs"],
      [0xfe, "abx"],
    ],
  ],
  [
    "DEC",
    [
      [0x3a, "acc"],
      [0xc6, "zp"],
      [0xd6, "zpx"],
      [0xce, "abs"],
      [0xde, "abx"],
    ],
  ],
]) {
  for (const [op, mode] of list) set(op, mn, mode);
}
for (const [op, mode] of [
  [0x14, "zp"],
  [0x1c, "abs"],
])
  set(op, "TRB", mode);
for (const [op, mode] of [
  [0x04, "zp"],
  [0x0c, "abs"],
])
  set(op, "TSB", mode);

// Register ops, transfers, flags, stack, NOP.
set(0xe8, "INX", "imp");
set(0xc8, "INY", "imp");
set(0xca, "DEX", "imp");
set(0x88, "DEY", "imp");
set(0xaa, "TAX", "imp");
set(0xa8, "TAY", "imp");
set(0x8a, "TXA", "imp");
set(0x98, "TYA", "imp");
set(0xba, "TSX", "imp");
set(0x9a, "TXS", "imp");
set(0x48, "PHA", "imp");
set(0x68, "PLA", "imp");
set(0x08, "PHP", "imp");
set(0x28, "PLP", "imp");
set(0xda, "PHX", "imp");
set(0xfa, "PLX", "imp");
set(0x5a, "PHY", "imp");
set(0x7a, "PLY", "imp");
set(0x18, "CLC", "imp");
set(0x38, "SEC", "imp");
set(0x58, "CLI", "imp");
set(0x78, "SEI", "imp");
set(0xd8, "CLD", "imp");
set(0xf8, "SED", "imp");
set(0xb8, "CLV", "imp");
set(0xea, "NOP", "imp");
// The W65C02S's UNDEFINED opcodes are NOPs of fixed lengths (WDC W65C02S
// datasheet, table 7-1): the x2 column takes an immediate byte, $44/$54/$D4/
// $F4 a zero-page one, $5C/$DC/$FC an absolute address — each READ and
// skipped, so a program holding one stays in step. Everything else undefined
// (the x3 and xB columns bar WAI/STP) is one byte.
for (const op of [0x02, 0x22, 0x42, 0x62, 0x82, 0xc2, 0xe2]) set(op, "NOPR", "imm"); // prettier-ignore
set(0x44, "NOPR", "zp");
for (const op of [0x54, 0xd4, 0xf4]) set(op, "NOPR", "zpx");
for (const op of [0x5c, 0xdc, 0xfc]) set(op, "NOPR", "abs");
set(0xcb, "WAI", "imp");
set(0xdb, "STP", "imp");

// Branches.
set(0x10, "BPL", "rel");
set(0x30, "BMI", "rel");
set(0x50, "BVC", "rel");
set(0x70, "BVS", "rel");
set(0x90, "BCC", "rel");
set(0xb0, "BCS", "rel");
set(0xd0, "BNE", "rel");
set(0xf0, "BEQ", "rel");
set(0x80, "BRA", "rel");

// Jumps / subroutine / interrupt.
set(0x4c, "JMP", "abs");
set(0x6c, "JMP", "ind");
set(0x7c, "JMP", "indx");
set(0x20, "JSR", "abs");
set(0x60, "RTS", "imp");
set(0x40, "RTI", "imp");
set(0x00, "BRK", "imp");

// Rockwell/WDC bit ops (BBRx/BBSx zp,rel; RMBx/SMBx zp).
for (let n = 0; n < 8; n++) {
  set(0x0f + n * 0x10, "BBR", "zprel", n);
  set(0x8f + n * 0x10, "BBS", "zprel", n);
  set(0x07 + n * 0x10, "RMB", "zp", n);
  set(0x87 + n * 0x10, "SMB", "zp", n);
}

/** The opcode table, for the CPU monitor's disassembler (sim/disasm-6502.js):
    `null` is an opcode that runs as a 1-byte NOP, `NOPR` one of the
    W65C02S's longer undefined NOPs. Read-only. */
export const W65C02_OPCODES = TABLE;

/** Run one whole instruction against the (replay) bus. `out.halt` ← WAI/STP. */
function execInstruction(cpu, bus, out) {
  const fetch = () => {
    const v = bus.read(cpu.pc);
    cpu.pc = (cpu.pc + 1) & 0xffff;
    return v;
  };
  const opcode = fetch();
  const e = TABLE[opcode] ?? { m: "NOP", a: "imp" };
  const { m, a } = e;
  // The 65C02 polls IRQ BEFORE an instruction's last cycle, so CLI, SEI and
  // PLP change what the NEXT poll sees one instruction late: an IRQ waiting
  // behind CLI runs one more instruction first, and one pending at SEI is
  // still taken once (RTI's restored I counts at once). `irqMask` is the I
  // the poll after this instruction uses.
  if (m === "CLI" || m === "SEI" || m === "PLP") out.irqMask = cpu.p & I;
  const ea = (kind) => effectiveAddr(cpu, bus, a, kind);
  // A dummy cycle: a read whose byte is thrown away.
  const idle = (addr) => void bus.read(addr & 0xffff);
  const stack = () => idle(0x100 + cpu.s);
  // A taken branch costs a cycle, and one more when it lands in another
  // page; both read the next instruction's address, the 65C02's harmless
  // choice (the NMOS part read a half-formed target).
  const branch = (cond) => {
    const off = signed(fetch());
    if (!cond) return;
    const to = (cpu.pc + off) & 0xffff;
    idle(cpu.pc);
    if ((to ^ cpu.pc) & 0xff00) idle(cpu.pc);
    cpu.pc = to;
  };
  // ADC/SBC in decimal mode take one cycle more on the 65C02 (it computes
  // valid N, V and Z): a read of the last instruction byte.
  const decimal = () => {
    if (getF(cpu, D)) idle(cpu.pc - 1);
  };
  // Every one-byte instruction — implied or accumulator, and BRK — reads the
  // byte after its opcode while it works, without moving PC. The W65C02S's
  // undefined one-byte opcodes are the exception: one cycle, nothing more.
  if (TABLE[opcode] && (a === "imp" || a === "acc")) idle(cpu.pc);

  switch (m) {
    case "LDA":
      cpu.a = bus.read(ea());
      setNZ(cpu, cpu.a);
      break;
    case "LDX":
      cpu.x = bus.read(ea());
      setNZ(cpu, cpu.x);
      break;
    case "LDY":
      cpu.y = bus.read(ea());
      setNZ(cpu, cpu.y);
      break;
    case "STA":
      bus.write(ea("write"), cpu.a);
      break;
    case "STX":
      bus.write(ea("write"), cpu.x);
      break;
    case "STY":
      bus.write(ea("write"), cpu.y);
      break;
    case "STZ":
      bus.write(ea("write"), 0);
      break;
    case "AND":
      cpu.a &= bus.read(ea());
      setNZ(cpu, cpu.a);
      break;
    case "ORA":
      cpu.a |= bus.read(ea());
      setNZ(cpu, cpu.a);
      break;
    case "EOR":
      cpu.a ^= bus.read(ea());
      setNZ(cpu, cpu.a);
      break;
    case "ADC":
      adc(cpu, bus.read(ea()));
      decimal();
      break;
    case "SBC":
      sbc(cpu, bus.read(ea()));
      decimal();
      break;
    case "CMP":
      cmp(cpu, cpu.a, bus.read(ea()));
      break;
    case "CPX":
      cmp(cpu, cpu.x, bus.read(ea()));
      break;
    case "CPY":
      cmp(cpu, cpu.y, bus.read(ea()));
      break;
    case "BIT":
      bitTest(cpu, bus.read(ea()), a);
      break;

    case "ASL":
    case "LSR":
    case "ROL":
    case "ROR":
    case "INC":
    case "DEC":
      // Memory: read, a dummy READ of the same address (the 65C02 re-reads
      // where the NMOS part wrote the old value back), write.
      if (a === "acc") cpu.a = rmwValue(cpu, m, cpu.a);
      else {
        const ad = ea(m === "INC" || m === "DEC" ? "inc" : "read");
        const v = bus.read(ad);
        idle(ad);
        bus.write(ad, rmwValue(cpu, m, v));
      }
      break;
    case "TRB":
    case "TSB": {
      const ad = ea();
      const v = bus.read(ad);
      idle(ad);
      setF(cpu, ZF, (cpu.a & v) === 0);
      bus.write(ad, m === "TSB" ? v | cpu.a : v & ~cpu.a & 0xff);
      break;
    }

    case "INX":
      cpu.x = (cpu.x + 1) & 0xff;
      setNZ(cpu, cpu.x);
      break;
    case "INY":
      cpu.y = (cpu.y + 1) & 0xff;
      setNZ(cpu, cpu.y);
      break;
    case "DEX":
      cpu.x = (cpu.x - 1) & 0xff;
      setNZ(cpu, cpu.x);
      break;
    case "DEY":
      cpu.y = (cpu.y - 1) & 0xff;
      setNZ(cpu, cpu.y);
      break;
    case "TAX":
      cpu.x = cpu.a;
      setNZ(cpu, cpu.x);
      break;
    case "TAY":
      cpu.y = cpu.a;
      setNZ(cpu, cpu.y);
      break;
    case "TXA":
      cpu.a = cpu.x;
      setNZ(cpu, cpu.a);
      break;
    case "TYA":
      cpu.a = cpu.y;
      setNZ(cpu, cpu.a);
      break;
    case "TSX":
      cpu.x = cpu.s;
      setNZ(cpu, cpu.x);
      break;
    case "TXS":
      cpu.s = cpu.x;
      break;

    case "PHA":
      push(cpu, bus, cpu.a);
      break;
    case "PHX":
      push(cpu, bus, cpu.x);
      break;
    case "PHY":
      push(cpu, bus, cpu.y);
      break;
    case "PHP":
      push(cpu, bus, cpu.p | B | U);
      break;
    case "PLA":
      stack();
      cpu.a = pull(cpu, bus);
      setNZ(cpu, cpu.a);
      break;
    case "PLX":
      stack();
      cpu.x = pull(cpu, bus);
      setNZ(cpu, cpu.x);
      break;
    case "PLY":
      stack();
      cpu.y = pull(cpu, bus);
      setNZ(cpu, cpu.y);
      break;
    case "PLP":
      stack();
      cpu.p = (pull(cpu, bus) & ~B) | U;
      break;

    case "CLC":
      setF(cpu, C, false);
      break;
    case "SEC":
      setF(cpu, C, true);
      break;
    case "CLI":
      setF(cpu, I, false);
      break;
    case "SEI":
      setF(cpu, I, true);
      break;
    case "CLD":
      setF(cpu, D, false);
      break;
    case "SED":
      setF(cpu, D, true);
      break;
    case "CLV":
      setF(cpu, V, false);
      break;

    case "BPL":
      branch(!getF(cpu, N));
      break;
    case "BMI":
      branch(getF(cpu, N));
      break;
    case "BVC":
      branch(!getF(cpu, V));
      break;
    case "BVS":
      branch(getF(cpu, V));
      break;
    case "BCC":
      branch(!getF(cpu, C));
      break;
    case "BCS":
      branch(getF(cpu, C));
      break;
    case "BNE":
      branch(!getF(cpu, ZF));
      break;
    case "BEQ":
      branch(getF(cpu, ZF));
      break;
    case "BRA":
      branch(true);
      break;

    case "JMP":
      if (a === "abs") {
        const lo = fetch();
        cpu.pc = lo | (fetch() << 8);
      } else {
        // JMP (a) and (a,X): six cycles on the 65C02, a dummy read of the
        // last operand byte before the pointer (which also cured the NMOS
        // part's page-wrap bug).
        const lo = fetch();
        let ptr = lo | (fetch() << 8);
        idle(cpu.pc - 1);
        if (a === "indx") ptr = (ptr + cpu.x) & 0xffff;
        cpu.pc = bus.read(ptr) | (bus.read((ptr + 1) & 0xffff) << 8);
      }
      break;
    case "JSR": {
      const lo = fetch(); // pc now points at the high byte
      stack(); // an internal cycle, on the stack
      push(cpu, bus, (cpu.pc >> 8) & 0xff);
      push(cpu, bus, cpu.pc & 0xff);
      cpu.pc = lo | (bus.read(cpu.pc) << 8);
      break;
    }
    case "RTS": {
      stack();
      const lo = pull(cpu, bus);
      const hi = pull(cpu, bus);
      idle((hi << 8) | lo); // the return address, before it is stepped on
      cpu.pc = (((hi << 8) | lo) + 1) & 0xffff;
      break;
    }
    case "RTI": {
      stack();
      cpu.p = (pull(cpu, bus) & ~B) | U;
      const lo = pull(cpu, bus);
      cpu.pc = ((pull(cpu, bus) << 8) | lo) & 0xffff;
      break;
    }
    case "BRK":
      doInterrupt(cpu, bus, "brk");
      break;

    case "BBR":
    case "BBS": {
      // Five cycles, then a taken branch's one or two.
      const z = fetch();
      const v = bus.read(z);
      idle(z);
      const bitSet = ((v >> e.bit) & 1) === 1;
      branch(m === "BBR" ? !bitSet : bitSet);
      break;
    }
    case "RMB":
    case "SMB": {
      const z = fetch();
      const v = bus.read(z);
      idle(z);
      bus.write(z, m === "RMB" ? v & ~(1 << e.bit) & 0xff : v | (1 << e.bit));
      break;
    }

    case "NOPR": // an undefined opcode with an operand: read, then nothing
      bus.read(ea());
      // $5C is the odd one: eight cycles, not four. Its last four are
      // spent here on reads of the next instruction's address, which nothing
      // decodes; WDC documents the count, not the addresses.
      if (opcode === 0x5c) for (let n = 0; n < 4; n++) idle(cpu.pc);
      break;
    case "WAI":
      idle(cpu.pc); // three cycles before it sleeps
      out.halt = "wai";
      break;
    case "STP":
      idle(cpu.pc);
      out.halt = "stp";
      break;
    default:
      break; // NOP
  }
}

/**
 * Run the reset sequence — seven cycles, an interrupt with its writes held
 * off: two reads of PC, three of the stack (S stepping down as for the
 * pushes it does not make, which is how S comes out of reset at $FD), then
 * the $FFFC/$FFFD vector into PC.
 */
function runReset(cpu, bus) {
  bus.read(cpu.pc);
  bus.read(cpu.pc);
  for (let n = 0; n < 3; n++) {
    bus.read(0x100 + cpu.s);
    cpu.s = (cpu.s - 1) & 0xff;
  }
  cpu.pc = bus.read(0xfffc) | (bus.read(0xfffd) << 8);
}

function runOp(cur, cpu, bus, out) {
  if (cur === "reset") runReset(cpu, bus);
  else if (cur === "nmi") doInterrupt(cpu, bus, "nmi");
  else if (cur === "irq") doInterrupt(cpu, bus, "irq");
  else execInstruction(cpu, bus, out);
}

const regsOf = (s) => ({ a: s.a, x: s.x, y: s.y, s: s.s, pc: s.pc, p: s.p });

/** Fresh power-on state: already IN reset, pending the first vector read. */
export function initialCpu() {
  return resetHold({ a: 0, x: 0, y: 0, pc: 0 }, { nmi: false });
}

/** The held reset state — registers initialised, the sequence's first read
    (of PC) on the bus until released. S starts at 0 so the sequence's three
    stack cycles leave it at $FD. */
function resetHold(s, ctl) {
  const pc = s.pc ?? 0;
  return {
    a: s.a ?? 0,
    x: s.x ?? 0,
    y: s.y ?? 0,
    s: 0,
    pc,
    p: I | U, // interrupts masked, decimal clear
    cur: "reset",
    log: [],
    addr: pc,
    rw: "r",
    dout: 0,
    sync: false,
    nmiPrev: Boolean(ctl.nmi),
    nmiPending: false,
  };
}

/** Begin the next operation (interrupt or instruction) → its first bus access. */
function beginNextOp(committed, base, nmiPending, ctl, irqMask) {
  let cur = "instr";
  let clearNmi = false;
  if (nmiPending) {
    cur = "nmi";
    clearNmi = true;
  } else if (ctl.irq && !(irqMask ?? committed.p & I)) {
    cur = "irq";
  }
  const cpu = regsOf(committed);
  const bus = makeBus([]);
  const out = {};
  try {
    runOp(cur, cpu, bus, out);
  } catch (ex) {
    if (ex !== SUSPEND) throw ex;
    return {
      ...cpu,
      ...base,
      nmiPending: clearNmi ? false : nmiPending,
      cur,
      log: [],
      addr: bus.pending.addr,
      rw: bus.pending.rw,
      dout: bus.pending.dout,
      // An interrupt's first cycle is the opcode fetch it abandons — a read
      // of PC with SYNC high like any other, so a SYNC + RDY single-stepper
      // or an analyzer sees it; the monitor tells it apart by `cur`.
      sync: true,
    };
  }
  throw new Error("W65C02: op produced no bus access");
}

/**
 * Advance the CPU one bus cycle. `busByte` is the data bus (used when the cycle
 * that just finished was a read); `ctl` carries the control-line levels
 * (`reset`/`irq`/`nmi` asserted booleans, `ready`). Pure — returns the next state.
 */
export function cpuCycle(state, busByte, ctl) {
  if (ctl.reset) return resetHold(state, ctl);

  const nmiPending = state.nmiPending || (!state.nmiPrev && Boolean(ctl.nmi));
  const base = { nmiPrev: Boolean(ctl.nmi) };

  if (ctl.ready === false) return { ...state, ...base, nmiPending }; // RDY stall
  if (state.cur === "stp") return { ...state, ...base, nmiPending }; // wait for reset
  if (state.cur === "wai") {
    // WAI wakes on NMI, IRQ, or RESET regardless of the I flag — a MASKED
    // IRQ still wakes the CPU, it just isn't serviced (beginNextOp's own
    // `ctl.irq && !(committed.p & I)` check below leaves `cur` as "instr" in
    // that case, so execution resumes at the next instruction rather than
    // jumping to the ISR). Reset is already handled by the `ctl.reset` guard
    // above, ahead of this branch.
    const wake = nmiPending || ctl.irq;
    if (!wake) return { ...state, ...base, nmiPending };
    return beginNextOp(state, base, nmiPending, ctl);
  }

  // Log the byte for the access that just completed, then re-run the current op.
  // `cpu` is a scratch copy the interpreter mutates during replay; the committed
  // registers (instruction start) stay in `state` and are what the NEXT cycle
  // replays from — so a mid-instruction suspend must keep state's regs, not cpu's.
  const log = [...state.log, state.rw === "r" ? busByte & 0xff : state.dout];
  const cpu = regsOf(state);
  const bus = makeBus(log);
  const out = {};
  try {
    runOp(state.cur, cpu, bus, out);
  } catch (ex) {
    if (ex !== SUSPEND) throw ex;
    return {
      ...regsOf(state), // instruction-start registers — NOT the partial `cpu`
      ...base,
      nmiPending,
      cur: state.cur,
      log,
      addr: bus.pending.addr,
      rw: bus.pending.rw,
      dout: bus.pending.dout,
      sync: false,
    };
  }
  // Instruction complete. WAI/STP halts; otherwise begin the next op.
  if (out.halt) {
    return {
      ...cpu,
      ...base,
      nmiPending,
      cur: out.halt,
      log: [],
      addr: cpu.pc,
      rw: "r",
      dout: 0,
      sync: false,
    };
  }
  return beginNextOp({ ...state, ...cpu }, base, nmiPending, ctl, out.irqMask);
}

// ── The registers as they stand mid-operation, for the CPU monitor ──────────

/**
 * The registers as the operation in flight has left them SO FAR: the
 * committed ones (the state keeps those until the operation ends) run
 * through the interpreter up to the access on the bus now — PC past the
 * bytes fetched, a loaded register once its byte is in. The same replay the
 * next cycle will make; pure. Outside an operation (WAI, STP), the state's.
 * @param {object} state
 * @returns {{a: number, x: number, y: number, s: number, pc: number, p: number}}
 */
export function liveRegisters(state) {
  const cpu = regsOf(state);
  if (!["instr", "irq", "nmi", "reset"].includes(state.cur)) return cpu;
  try {
    runOp(state.cur, cpu, makeBus(state.log), {});
  } catch (ex) {
    if (ex !== SUSPEND) throw ex;
  }
  return cpu;
}

/**
 * The W65C02 as an engine sequential unit. Pin params are catalog pin numbers;
 * `addr` is [A0…A15] and `data` is [D0…D7], both LSB first.
 * @returns {{ state0: Function, step: Function, outputs: Function }}
 */
export function w65c02Unit(pins) {
  const {
    addr,
    data,
    rwb,
    sync,
    phi2,
    resb,
    irqb,
    nmib,
    rdy,
    be,
    vpb,
    mlb,
    phi1o,
    phi2o,
  } = pins;

  const readData = (ins) =>
    data.reduce((n, pin, i) => n | ((ins.get(pin) === H ? 1 : 0) << i), 0);

  return {
    state0: initialCpu,

    step(state, ins, prev) {
      const now = ins.get(phi2);
      const was = prev ? prev.get(phi2) : undefined;
      if (!(was === H && now === L)) return state; // advance on PHI2's falling edge
      const ctl = {
        reset: ins.get(resb) === L,
        irq: ins.get(irqb) === L,
        nmi: ins.get(nmib) === L,
        ready: ins.get(rdy) !== L,
      };
      // The byte is taken from `prev` — the bus as it stood while PHI2 was
      // still HIGH — not from `ins`, which is this falling edge's own settle.
      // A 6502 samples read data on PHI2's TRAILING edge, i.e. the value the
      // addressed part was holding up during the high phase, and the two are
      // not the same thing here: an ASYNCHRONOUS part (ROM, SRAM) drives on
      // /CE alone and so reads the same either way, but every 65xx PERIPHERAL
      // gates its bus drivers on PHI2 and has already released by the time
      // this settle runs. Reading `ins` therefore returned a floating bus —
      // $FF — for every VIA and PIA register in the app, which is why a
      // button read as "not pressed" however it was wired. The address and
      // R/W̄ are unchanged across both phases of a cycle (they are committed
      // at the PREVIOUS falling edge), so `prev` names the same access.
      return cpuCycle(state, readData(prev), ctl);
    },

    outputs(state, ins) {
      const out = new Map();
      const enabled = ins.get(be) !== L; // BE low tri-states the bus outputs
      addr.forEach((pin, i) =>
        out.set(pin, enabled ? ((state.addr >> i) & 1 ? H : L) : Z),
      );
      out.set(rwb, enabled ? (state.rw === "w" ? L : H) : Z);
      if (enabled && state.rw === "w") {
        data.forEach((pin, i) => out.set(pin, (state.dout >> i) & 1 ? H : L));
      } else {
        for (const pin of data) out.set(pin, Z);
      }
      out.set(sync, state.sync ? H : L);
      out.set(vpb, H); // vector-pull indicator — inactive (cosmetic)
      out.set(mlb, H); // memory-lock indicator — inactive (cosmetic)
      const clk = ins.get(phi2);
      out.set(phi2o, clk === L ? L : H);
      out.set(phi1o, clk === L ? H : L);
      return out;
    },
  };
}
